import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';

interface CivitaDevice {
    _id: string;
    macAddress: string;
    deviceId: string;
    deviceModel: string;
    status: 'pending' | 'accepted' | 'rejected';
    lastSeen: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CivitaControl: React.FC = () => {
    const [devices, setDevices] = useState<CivitaDevice[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchDevices = async () => {
        try {
            setLoading(true);
            setError(null);
            // Append /api to base URL if REACT_APP_API_URL doesn't contain it
            const baseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
            const res = await fetch(`${baseUrl}/civita-devices`);
            if (!res.ok) throw new Error('Failed to fetch Civita devices');

            const data = await res.json();
            if (data.success) {
                setDevices(data.data);
            } else {
                throw new Error(data.message || 'Error fetching devices');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const updateDeviceStatus = async (macAddress: string, newStatus: 'accepted' | 'rejected') => {
        try {
            const baseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
            const res = await fetch(`${baseUrl}/civita-devices/${encodeURIComponent(macAddress)}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });

            if (!res.ok) throw new Error(`Failed to update status to ${newStatus}`);

            const data = await res.json();
            if (data.success) {
                // Update local state without full refetch
                setDevices(prev =>
                    prev.map(d => d.macAddress === macAddress ? { ...d, status: newStatus } : d)
                );
            } else {
                throw new Error(data.message || 'Error updating device status');
            }
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        }
    };

    const deleteDevice = async (macAddress: string) => {
        if (!window.confirm(`Sei sicuro di voler cancellare definitivamente il dispositivo con MAC: ${macAddress}?`)) {
            return;
        }

        try {
            const baseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
            const res = await fetch(`${baseUrl}/civita-devices/${encodeURIComponent(macAddress)}`, {
                method: 'DELETE',
            });

            if (!res.ok) throw new Error('Failed to delete device');

            const data = await res.json();
            if (data.success) {
                // Remove device from local state
                setDevices(prev => prev.filter(d => d.macAddress !== macAddress));
            } else {
                throw new Error(data.message || 'Error deleting device');
            }
        } catch (err: any) {
            alert(`Errore durante l'eliminazione: ${err.message}`);
        }
    };

    useEffect(() => {
        fetchDevices();
    }, []);

    const getStatusChip = (status: string) => {
        switch (status) {
            case 'accepted': return <Chip label="Accettato" color="success" size="small" />;
            case 'rejected': return <Chip label="Rifiutato" color="error" size="small" />;
            default: return <Chip label="In attesa" color="warning" size="small" />;
        }
    };

    if (loading && devices.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Controllo Civita
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Gestisci i visori e i dispositivi per l'esperienza "Civita".
                I dispositivi devono essere accettati qui per poter avviare l'esperienza.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper} elevation={3}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ backgroundColor: 'rgba(212, 175, 55, 0.1)' }}>
                        <TableRow>
                            <TableCell><strong>Modello Dispositivo</strong></TableCell>
                            <TableCell><strong>Indirizzo MAC</strong></TableCell>
                            <TableCell><strong>ID Dispositivo</strong></TableCell>
                            <TableCell><strong>Ultimo Accesso</strong></TableCell>
                            <TableCell><strong>Stato</strong></TableCell>
                            <TableCell align="center"><strong>Azioni</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {devices.map((device) => (
                            <TableRow key={device._id} hover>
                                <TableCell>{device.deviceModel}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{device.macAddress}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85em' }} title={device.deviceId}>
                                    {device.deviceId.substring(0, 15)}...
                                </TableCell>
                                <TableCell>{new Date(device.lastSeen).toLocaleString('it-IT')}</TableCell>
                                <TableCell>{getStatusChip(device.status)}</TableCell>
                                <TableCell align="center">
                                    <Box display="flex" justifyContent="center" gap={1}>
                                        {device.status !== 'accepted' && (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={() => updateDeviceStatus(device.macAddress, 'accepted')}
                                            >
                                                Accetta
                                            </Button>
                                        )}
                                        {device.status !== 'rejected' && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => updateDeviceStatus(device.macAddress, 'rejected')}
                                            >
                                                Revoca
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            onClick={() => deleteDevice(device.macAddress)}
                                        >
                                            Elimina
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {devices.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Nessun dispositivo Civita registrato al momento.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default CivitaControl;
